import { Link, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X, Minus, Plus, ShoppingBag } from "lucide-react";
import { useCart } from "../context/CartContext";
import { BottleVisual } from "./BottleVisual";
import { EASE } from "./Reveal";

export function CartDrawer() {
  const { items, isOpen, setIsOpen, setQty, removeItem, subtotal } = useCart();
  const navigate = useNavigate();

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            data-testid="cart-backdrop"
            className="fixed inset-0 z-[70] bg-stone-950/40 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={() => setIsOpen(false)}
          />
          <motion.aside
            data-testid="cart-drawer"
            className="fixed right-0 top-0 z-[80] flex h-full w-full max-w-md flex-col bg-[#FBF9F5] shadow-2xl"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.5, ease: EASE }}
            aria-label="Shopping bag"
          >
            <div className="flex items-center justify-between border-b border-stone-900/10 px-6 py-5">
              <p className="font-mono text-[11px] font-medium uppercase tracking-[0.25em] text-stone-900">
                Your Bag
              </p>
              <button
                type="button"
                data-testid="cart-close-button"
                onClick={() => setIsOpen(false)}
                className="p-1.5 text-stone-600 transition-colors duration-300 hover:text-stone-950"
                aria-label="Close bag"
              >
                <X className="h-5 w-5" strokeWidth={1.5} />
              </button>
            </div>

            {items.length === 0 ? (
              <div className="flex flex-1 flex-col items-center justify-center gap-5 px-8 text-center">
                <ShoppingBag className="h-10 w-10 text-stone-300" strokeWidth={1} />
                <p className="font-serif text-2xl font-light text-stone-800">Your bag is empty.</p>
                <Link
                  to="/shop"
                  data-testid="cart-empty-shop-link"
                  onClick={() => setIsOpen(false)}
                  className="border border-stone-900 px-7 py-3 font-mono text-[11px] font-medium uppercase tracking-[0.22em] text-stone-900 transition-colors duration-300 hover:bg-stone-900 hover:text-stone-50"
                >
                  Shop Nº 01
                </Link>
              </div>
            ) : (
              <>
                <div className="flex-1 overflow-y-auto px-6 py-6">
                  <ul className="space-y-6">
                    {items.map((item) => (
                      <li key={item.id} data-testid={`cart-item-${item.id}`} className="flex gap-4 border-b border-stone-900/10 pb-6">
                        <div className="flex h-24 w-20 shrink-0 items-center justify-center border border-stone-900/10 bg-[#F3EFE6] p-2">
                          <BottleVisual className="h-full w-auto" shadow={false} />
                        </div>
                        <div className="flex flex-1 flex-col">
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-emerald-900/80">
                                {item.edition}
                              </p>
                              <p className="mt-1 font-serif text-lg leading-tight text-stone-900">{item.name}</p>
                            </div>
                            <button
                              type="button"
                              data-testid={`cart-remove-${item.id}`}
                              onClick={() => removeItem(item.id)}
                              className="p-1 text-stone-400 transition-colors duration-300 hover:text-stone-900"
                              aria-label={`Remove ${item.name}`}
                            >
                              <X className="h-4 w-4" strokeWidth={1.5} />
                            </button>
                          </div>
                          <div className="mt-auto flex items-center justify-between pt-3">
                            <div className="flex items-center border border-stone-900/20">
                              <button
                                type="button"
                                data-testid={`cart-qty-minus-${item.id}`}
                                onClick={() => setQty(item.id, item.qty - 1)}
                                className="px-2.5 py-1.5 text-stone-700 transition-colors duration-200 hover:bg-stone-900 hover:text-stone-50"
                                aria-label="Decrease quantity"
                              >
                                <Minus className="h-3.5 w-3.5" />
                              </button>
                              <span data-testid={`cart-qty-${item.id}`} className="w-8 text-center font-mono text-xs">
                                {item.qty}
                              </span>
                              <button
                                type="button"
                                data-testid={`cart-qty-plus-${item.id}`}
                                onClick={() => setQty(item.id, item.qty + 1)}
                                className="px-2.5 py-1.5 text-stone-700 transition-colors duration-200 hover:bg-stone-900 hover:text-stone-50"
                                aria-label="Increase quantity"
                              >
                                <Plus className="h-3.5 w-3.5" />
                              </button>
                            </div>
                            <p className="font-mono text-sm text-stone-900">£{(item.price * item.qty).toFixed(2)}</p>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="border-t border-stone-900/10 px-6 py-6">
                  <div className="flex items-center justify-between">
                    <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-stone-600">Subtotal</p>
                    <p data-testid="cart-subtotal" className="font-serif text-2xl text-stone-900">
                      £{subtotal.toFixed(2)}
                    </p>
                  </div>
                  <p className="mt-2 text-xs text-stone-500">UK delivery calculated at checkout. Demo store — no payment is taken.</p>
                  <button
                    type="button"
                    data-testid="proceed-to-checkout-button"
                    onClick={() => {
                      setIsOpen(false);
                      navigate("/checkout");
                    }}
                    className="mt-5 w-full bg-stone-900 px-8 py-4 font-mono text-[11px] font-medium uppercase tracking-[0.25em] text-stone-50 transition-colors duration-300 hover:bg-emerald-900"
                  >
                    Proceed to Checkout
                  </button>
                </div>
              </>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
